import { Button } from "@components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { Input } from "@components/ui/input";
import React, { SetStateAction, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { formSchema, InviteStaffFormValues } from "./inviteStaffForm.types";
import { zodResolver } from "@hookform/resolvers/zod";
import { inviteHiringStaff } from "@endpoints/hiringCompany/hiringCompany";
import { useSnackbar } from "notistack";
import LoadingAnimation from "@assets/svg/LoadingAnimation";

type InviteMemberDialogProps = {
  isInviteDialogOpen: boolean;
  setIsInviteDialogOpen: React.Dispatch<SetStateAction<boolean>>;
};

const InviteMemberDialog = (props: InviteMemberDialogProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { isInviteDialogOpen, setIsInviteDialogOpen } = props;

  const { control, handleSubmit } = useForm<InviteStaffFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
    },
  });
  const [isInviting, setIsInviting] = useState(false);

  const handleSave = async (data: any) => {
    try {
      setIsInviting(true);
      const { success } = await inviteHiringStaff(data);
      if (success) {
        setIsInviteDialogOpen(false)
        enqueueSnackbar("Invitation sent successfully.", { variant: "success" });
      }
    } catch (error) {
      enqueueSnackbar(error || "Something went wrong", {
        variant: "error",
      });
    } finally {
      setIsInviting(true);
    }
  };

  return (
    <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
      <DialogContent className="bg-white w-[340px] max-w-[340px]">
        <DialogHeader>
          <DialogTitle>Invite Member</DialogTitle>
          <DialogDescription className="font-medium text-xs text-light-black">
            Send invitations for staff onboarding.{" "}
          </DialogDescription>
        </DialogHeader>
        <form className="flex flex-col gap-4 my-1">
          <div className="flex itesm-center gap-3">
            <Controller
              name="firstName"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-2 flex-1">
                  <span className="font-medium text-xs">First Name</span>
                  <Input
                    {...field}
                    placeholder="Enter your first name"
                    className="bg-gray-600 !text-xs"
                  />
                  {fieldState.error && (
                    <p className="text-red-500 text-xs">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            />
            <Controller
              name="lastName"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-2 flex-1">
                  <span className="font-medium text-xs">Last Name</span>
                  <Input
                    {...field}
                    placeholder="Enter your last name"
                    className="bg-gray-600 !text-xs"
                  />
                  {fieldState.error && (
                    <p className="text-red-500 text-xs">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>
          <Controller
            name="email"
            control={control}
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-2">
                <span className="font-medium text-xs">
                  Company's email address
                </span>
                <Input
                  {...field}
                  placeholder="Enter your email"
                  className="bg-gray-600 !text-xs"
                />
                {fieldState.error && (
                  <p className="text-red-500 text-xs">
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />
        </form>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button
            onClick={handleSubmit(handleSave)}
            type="submit"
            className="text-white gap-3"
          >
            Save
            {isInviting ? <LoadingAnimation className="w-4 h-4" /> : null}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InviteMemberDialog;
